<template>
  <div :class="$style.mailContent">
    <div v-purify-html="parts.body" />
    <div
      v-if="parts.quote"
      :class="$style.quote"
    >
      <button
        :class="$style.toggleBtn"
        @click="toggleQuoteVisibility"
      >
        {{ $trlMessage('email.show.full') }}
      </button>
      <div
        v-if="isQuoteVisible"
        v-purify-html="parts.quote"
        :class="$style.quoteContent"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'MailContent',
  props: {
    content: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isQuoteVisible: false
    };
  },
  computed: {
    parts() {
      return splitMailContent(this.content);
    }
  },
  methods: {
    toggleQuoteVisibility() {
      this.isQuoteVisible = !this.isQuoteVisible;
    }
  }
};

// Рег. выражения для того, чтобы определить строку вида
// Что-то <email@email.email> что-то
// для того, чтобы если это предшевствует цитате, то переносим в цитату
// регулярка взята отсюда
const emailQuoteRegexp

  = /<(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))>/;

/*
 *  разделяет html письма на два блока:
 * - тело письма
 * - цитата
 */
function splitMailContent(mailContent) {
  const isEmptyLineNode = (node) => {
    if (!node) {
      return false;
    }
    if (node.tagName === 'BR') {
      return true;
    }
    if (['P', 'DIV'].includes(node.tagName) === false) {
      return false;
    }
    if (!node.firstChild) {
      return false;
    }

    if (node.firstChild === node.lastChild) {
      if (node.firstChild instanceof HTMLBRElement) {
        return true;
      }
      if (node.firstChild.nodeType === Element.TEXT_NODE) {
        return node.firstChild.textContent.replace(/\s+/g, '') === '';
      }
    }
    return false;
  };

  let body = mailContent;
  let quote;

  const root = document.createElement('div');
  root.innerHTML = mailContent;

  // бэк заворачивает в div - нужно развернуть
  if (root.childElementCount === 1 && root.firstChild instanceof HTMLDivElement) {
    root.innerHTML = root.firstChild.innerHTML;
  }

  const bloquoteTags = Array.from(root.querySelectorAll('blockquote:last-of-type'));
  // мы будем проверять только последний верхнеуровневый тег
  const testBloquoteTag = getTopLevelNode(bloquoteTags, root);
  if (testBloquoteTag) {
    const nextSiblings = getAllNextSiblings(testBloquoteTag);
    if (nextSiblings.length === 0) {
      // некоторые п. клиенты перед цитированием добавляют строку датой и почтой,
      // её нужно переместить под цитату
      const elemBeforeQuote = testBloquoteTag.previousElementSibling;
      // eslint-disable-next-line unicorn/prefer-dom-node-text-content
      if (elemBeforeQuote && emailQuoteRegexp.test(elemBeforeQuote.innerText)) {
        const fstQuoteChild = testBloquoteTag.firstChild;
        testBloquoteTag.insertBefore(elemBeforeQuote, fstQuoteChild);
      }
      quote = testBloquoteTag.outerHTML;
      testBloquoteTag.parentNode.removeChild(testBloquoteTag);
      // если есть цитата, то перед ней удаляем "пустые" строки (параграфы, в которых только BR)
      while (isEmptyLineNode(root.lastChild)) {
        root.removeChild(root.lastChild);
      }
      body = root.innerHTML;
    }
  }

  return {
    body,
    quote
  };
}

/*
 * возвращаем всё следующее за domNode
 * в нашем кейсе если что-то следует за тегом blockquote,
 * тогда этот тег не прячем под кат
 */
function getAllNextSiblings(domNode) {
  const result = [];
  let node = domNode;
  while (node.nextSibling) {
    const sibling = node.nextSibling;
    if (!isEmptyTextNode(sibling)) {
      result.push(sibling);
    }
    node = sibling;
  }
  return result;
}

/*
 * теоретически (и скорее всего) после узла могут идти пробелы-переносы
 * их не надо учитывать за sibling-элемент
 */
function isEmptyTextNode(node) {
  return (
    node.nodeType === Node.TEXT_NODE && node.textContent.replace(/(\s|\r|\n)*/g, '').length === 0
  );
}

function getTopLevelNode(domNodes, rootNode) {
  // первым элементом будет последний по индексу, но с наименьшим lvl
  const sortComparator = (a, b) => {
    if (a.lvl === b.lvl) {
      return b.index - a.index;
    }
    return a.lvl - b.lvl;
  };

  const nodesWithLevel = domNodes.map((node, index) => ({
    node,
    index, // для устойчивости сортировки
    lvl: getNodeNestingLevel(node, rootNode)
  }));

  return nodesWithLevel.sort(sortComparator)[0]?.node;
}

function getNodeNestingLevel(domNode, rootNode) {
  let node = domNode;
  let level = 0;
  while (node !== rootNode) {
    node = node.parentNode;
    if (node.tagName === domNode.tagName) {
      level += 1;
    }
  }
  return level;
}
</script>

<style module>
.toggleBtn {
  @mixin reset-button-styles;
  font-size: 16px;
  line-height: 22px;
  color: #2cc8df;
  cursor: pointer;

  &:hover {
    color: $red-400;
  }
}

.quote {
  margin-top: 18px;
  margin-bottom: 23px;
}

.mailContent {
  overflow-y: auto;
  word-wrap: break-word;
}

.mailContent .quoteContent {
  margin: 15px 0 0 0;
}

.mailContent blockquote {
  margin: 10px 0;
}

.mailContent blockquote {
  display: block;
  padding: 0 0 0 10px;
  border-left: 1px solid #aeaade;
  & blockquote {
    margin: 20px 0 0 0;
  }
  &,
  & * {
    color: #8882d3;
  }
}

.mailContent blockquote td {
  border-color: #8882d3 !important;
}

.mailContent a {
  color: $blueColor;
  &:hover {
    color: $red-400;
  }
}
</style>

<i18n lang="json">{}</i18n>
